import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { getMainDefinition } from '@apollo/client/utilities';
import { Kind, OperationTypeNode } from 'graphql';

import generatedIntrospection from '@/graphql/generated/fragment';
import { isIpad } from '@/utils/user_agent';

type HeadersInit = Headers | string[][] | Record<string, string>;

// 端末の上りが止まるとサーバ(Puma)が 408 を返すまで30秒かかり、その間ずっと画面が固まる。
// 通常時の p99.9 は 0.355秒なので、十分離したこの値で先に打ち切る。
const REQUEST_TIMEOUT_MS = 10_000;

class RequestTimeoutError extends Error {
  constructor() {
    super('リクエストがタイムアウトしました');
    this.name = 'RequestTimeoutError';
  }
}

const isQueryOperation = (operation: ApolloLink.Operation) => {
  const definition = getMainDefinition(operation.query);
  return (
    definition.kind === Kind.OPERATION_DEFINITION &&
    definition.operation === OperationTypeNode.QUERY
  );
};

// ApolloClient は LiffProvider で liff.init が済み userId が取れてから作るので、ここでは初期化済みの前提で呼べる。
// 取れなくても従来どおり送る（サーバーはまだ ID トークンで拒否しない）
const getLiffIdToken = async () => {
  try {
    const liff = await import('@line/liff').then(module => module.default);
    return liff.getIDToken();
  } catch {
    return null;
  }
};

/**
 * @see https://www.apollographql.com/docs/react/api/link/apollo-link-batch-http/#dynamic-uri
 */
const fetchWithLiffIdAndUserId = async (
  uri: RequestInfo | URL,
  options?: RequestInit,
  liffId?: string,
  userId?: string
): Promise<Response> => {
  const { timeoutMs, ...restOptions } = (options ?? {}) as RequestInit & {
    timeoutMs?: number;
  };
  const newOptions: RequestInit = { ...restOptions };
  // bodyが存在し、JSONとしてパースできる場合はliffIdとuserIdをbodyに追加
  if (restOptions.body) {
    try {
      const bodyObj: unknown = JSON.parse(restOptions.body as string);
      // liffIdとuserIdをbodyに追加
      if (Array.isArray(bodyObj)) {
        bodyObj.forEach(obj => {
          if (typeof obj === 'object' && obj !== null) {
            (obj as Record<string, unknown>)['liffId'] = liffId;
            if (userId) {
              (obj as Record<string, unknown>)['userId'] = userId;
            }
          }
        });
      } else if (typeof bodyObj === 'object' && bodyObj !== null) {
        (bodyObj as Record<string, unknown>)['liffId'] = liffId;
        if (userId) {
          (bodyObj as Record<string, unknown>)['userId'] = userId;
        }
      }
      newOptions.body = JSON.stringify(bodyObj);
    } catch {
      // パースできなければ何もしない
    }
  }

  if (timeoutMs === undefined) {
    return fetch(uri, newOptions);
  }

  // AbortController はリトライのたびに作り直す（使い回すと2回目以降に abort 済みの signal を渡してしまう）。
  // HttpLink が渡してくる signal は AbortSignal.any が WebView で使えるか読めないため手で繋ぐ
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const abortByCaller = () => controller.abort();
  restOptions.signal?.addEventListener('abort', abortByCaller);
  // 登録前に abort 済みだと abort イベントはもう飛んでこない
  if (restOptions.signal?.aborted) controller.abort();

  try {
    return await fetch(uri, { ...newOptions, signal: controller.signal });
  } catch (error) {
    // 呼び出し元の中断は再送しないので、自前のタイムアウトと区別する
    throw timedOut ? new RequestTimeoutError() : error;
  } finally {
    clearTimeout(timer);
    restOptions.signal?.removeEventListener('abort', abortByCaller);
  }
};

export const createApolloClient = (liffId: string, userId?: string) => {
  const httpLink = new HttpLink({
    uri: `${process.env.apiBaseUrl}/line/graphql`,
    fetch: (uri, options) =>
      fetchWithLiffIdAndUserId(uri, options, liffId, userId),
  });

  const authLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }: { headers: HeadersInit }) => {
      headers = {
        ...headers,
        'X-Pharms-Service': 'kain',
        // TODO: Datadog をやめるときにヘッダー名を変える（api/config/initializers/opentelemetry.rb）
        'X-Datadog-Env': 'kain-request',
      };

      if (isIpad()) {
        // kinesissToBigQueryでdevice判定してる処理を将来的にフロントに移す予定
        // iPadOSだけはdevice判定できないため、先に移した
        headers = {
          ...headers,
          'X-Device-Type': 'tablet',
        };
      }

      const contextHeader = {
        headers: {
          ...headers,
        },
      };
      return contextHeader;
    });

    return forward(operation);
  });

  // getIDToken は LIFF が持っているトークンを返すだけで更新はしないため、期限切れ（発行から1時間）はここでは防げない。
  // 期限切れの扱いは、サーバーで拒否を始める段階で入れる
  const idTokenLink = new SetContextLink(async ({ headers }) => {
    const idToken = await getLiffIdToken();
    // 取れなかったことをサーバーに伝え、この処理を含まない古い画面（何も付かない）と分けて数えられるようにする
    if (!idToken) {
      return { headers: { ...headers, 'X-Kain-Id-Token': 'unavailable' } };
    }

    return {
      headers: { ...headers, Authorization: `Bearer ${idToken}` },
    };
  });

  // 予約作成など冪等でない mutation を途中で切ると二重登録を招くため、query だけ打ち切る
  const timeoutLink = new ApolloLink((operation, forward) => {
    if (isQueryOperation(operation)) {
      operation.setContext(
        ({ fetchOptions }: { fetchOptions?: RequestInit }) => ({
          fetchOptions: { ...fetchOptions, timeoutMs: REQUEST_TIMEOUT_MS },
        })
      );
    }
    return forward(operation);
  });

  const retryLink = new RetryLink({
    delay: { initial: 300, jitter: true },
    attempts: {
      // 初回リクエストを含むので、再送は1回
      max: 2,
      // 408 は useSuspenseQuery 側で既に1回再送されるため、ここで拾うと二重になる。
      // TypeError は回線断・回線切替（CORS 等も含む）で fetch が失敗したもの
      retryIf: (error, operation) =>
        (error instanceof RequestTimeoutError || error instanceof TypeError) &&
        isQueryOperation(operation),
    },
  });

  const errorLink = new ErrorLink(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message, locations, path }) => {
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${JSON.stringify(path)}`
        );
      });
    } else {
      console.error(
        `[Network error]: ${error instanceof Error ? error.message : JSON.stringify(error)}`
      );
    }
  });

  const cache = new InMemoryCache({
    possibleTypes: generatedIntrospection.possibleTypes,
  });

  return new ApolloClient({
    ssrMode: false,
    // authLink はリトライの内側に置き、再送のたびにヘッダを組み直させる
    link: ApolloLink.from([
      errorLink,
      retryLink,
      authLink,
      idTokenLink,
      timeoutLink,
      httpLink,
    ]),
    cache,
    defaultOptions: {
      query: {
        errorPolicy: 'all',
      },
    },
    devtools: {
      enabled: true,
    },
  });
};
